import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import AskTeamAssistV2, { TEAM_ASSIST_V2_QUESTION_MAX_LENGTH } from 'actions/team_assist_v2/ask_team_assist_v2';
import ClearTeamAssistV2 from 'actions/team_assist_v2/clear_team_assist_v2';
import { TeamAssistV2AnswerStatus } from 'actions/team_assist_v2/lib/team_assist_v2_answer';
import RateTeamAssistV2Answer from 'actions/team_assist_v2/rate_team_assist_v2_answer';
import RequestTeamAssistV2 from 'actions/team_assist_v2/request_team_assist_v2';
import RetryTeamAssistV2 from 'actions/team_assist_v2/retry_team_assist_v2';
import ToggleTeamAssistPanel from 'actions/team_assist/toggle_team_assist_panel';
import UseTeamAssistV2Reply from 'actions/team_assist_v2/use_team_assist_v2_reply';
import connect from 'components/lib/connect';
import VisuallyHidden from 'components/common/visually_hidden';
import TeamAssistEmptyStateIcon from 'components/customer/team_assist/team_assist_empty_state_icon';
import {
  AssistantRow,
  ErrorIcon,
  ErrorRetryButton,
  ErrorRow,
  ErrorText,
} from 'components/customer/team_assist/team_assist_message_parts';
import {
  CannedResponseButton,
  CannedResponseButtons,
  EmptyState,
  EmptyStateIconWrapper,
  EmptyStateText,
  Header,
  HeaderActions,
  HeaderSubtitle,
  HeaderText,
  HeaderTextWrap,
  InputArea,
  MessagesArea,
  StyledHeaderIcon,
  StyledPanel,
  TeamAssistActionsDropdown,
  TeamAssistActionsMenuIcon,
  TeamAssistActionsMenuOpener,
  TeamAssistInput,
  TeamAssistPanelActionsMenu,
} from 'components/customer/team_assist/team_assist_panel_shared';
import { TeamAssistLoadingDots } from 'components/customer/team_assist/team_assist_shared';
import TeamAssistV2Turn from 'components/customer/team_assist_v2/team_assist_v2_turn';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import gladlyCdn from 'scripts/lib/gladly_cdn';

export function TeamAssistV2Panel({
  cannedResponses,
  cdnHost,
  conversationId,
  customerId,
  customerName,
  hasLoaded,
  isClearing,
  isLoading,
  hasLoadFailed,
  orgId,
  turns,
}) {
  const { i18n, t } = useTranslation();
  const executeAction = useExecuteAction();
  // The session loads with the customer, but a panel that appears afterward
  // (teamAssistV2 turned on meanwhile) finds nothing loaded, so it asks once.
  const hasRequestedRef = useRef(false);
  useEffect(() => {
    if (hasRequestedRef.current || hasLoaded || isLoading || hasLoadFailed || isClearing) {
      return;
    }
    hasRequestedRef.current = true;
    executeAction(RequestTeamAssistV2, { customerId });
  }, [customerId, executeAction, hasLoaded, isLoading, hasLoadFailed, isClearing]);
  const [inputValue, setInputValue] = useState('');
  const messagesAreaRef = useRef(null);
  const inputRef = useRef(null);
  const isStreaming = turns.some(turn => turn.live);
  const showLoadFailure = hasLoadFailed && turns.length === 0;
  // A question waits until nothing is streaming and any Clear has answered,
  // and until the session has loaded, so it never continues a session whose
  // earlier turns aren't shown.
  const isBusy = isStreaming || isClearing || isLoading || showLoadFailure;
  const isAtLengthLimit = inputValue.length >= TEAM_ASSIST_V2_QUESTION_MAX_LENGTH;
  const isEmpty = turns.length === 0 && !isLoading && !showLoadFailure;
  const visibleCannedResponses = visibleQuickActions(cannedResponses, { hasTurns: turns.length > 0, isLoading });
  const lastTurn = turns.length ? turns[turns.length - 1] : null;
  const lastAnswer = lastTurn ? lastTurn.answer : null;
  const onMessagesScroll = useFollowNewestAnswer(messagesAreaRef, { lastAnswer, lastTurnId: lastTurn && lastTurn.id });
  const hasJustAnswered = useHasJustAnswered(lastAnswer);
  const focusInput = useCallback(() => inputRef.current && inputRef.current.focus(), []);

  const ask = useCallback(
    (question, displayedMessage) => {
      if (!question || isBusy) {
        return;
      }
      executeAction(AskTeamAssistV2, { conversationId, customerId, displayedMessage, question });
    },
    [conversationId, customerId, executeAction, isBusy]
  );

  const handleSubmit = useCallback(() => {
    const question = inputValue.trim();
    if (!question || isBusy) {
      return;
    }
    setInputValue('');
    ask(question);
  }, [ask, inputValue, isBusy]);

  // Retry goes away as the load starts, so focus moves to the question box.
  const reload = useCallback(() => {
    executeAction(RequestTeamAssistV2, { customerId });
    focusInput();
  }, [customerId, executeAction, focusInput]);

  const onKeyDown = useCallback(
    evt => {
      if (evt.key === 'Enter' && !evt.shiftKey) {
        evt.preventDefault();
        handleSubmit();
      } else if (evt.key === 'Escape') {
        executeAction(ToggleTeamAssistPanel);
      }
    },
    [executeAction, handleSubmit]
  );

  const retry = useCallback(
    turn => {
      executeAction(RetryTeamAssistV2, { customerId, turnId: turn.id });
      focusInput();
    },
    [customerId, executeAction, focusInput]
  );

  const rate = useCallback(
    ({ rating, turnId }) => executeAction(RateTeamAssistV2Answer, { customerId, rating, turnId }),
    [customerId, executeAction]
  );

  const insertReply = useCallback(
    ({ html, messageId }) => executeAction(UseTeamAssistV2Reply, { customerId, html, messageId }),
    [customerId, executeAction]
  );

  const onSelectAction = useCallback(
    value => {
      if (value === 'clear-session') {
        executeAction(ClearTeamAssistV2, { customerId });
        focusInput();
      }
    },
    [customerId, executeAction, focusInput]
  );

  return (
    <StyledPanel data-aid="teamAssistV2Panel" tabIndex="0">
      <Header>
        <StyledHeaderIcon height="16" width="16" />
        <HeaderTextWrap>
          <HeaderText>{t('Team Assist')}</HeaderText>
          <HeaderSubtitle>{customerName || t('this customer')}</HeaderSubtitle>
        </HeaderTextWrap>
        <HeaderActions>
          {turns.length > 0 && !isClearing && (
            <TeamAssistActionsDropdown
              data-aid="teamAssistV2ActionsMenu"
              menuComponent={TeamAssistPanelActionsMenu}
              onSelect={onSelectAction}
              openerComponent={TeamAssistActionsMenuOpener}
              options={[{ label: t('Clear session'), value: 'clear-session' }]}
              position="bottom"
              text={<TeamAssistActionsMenuIcon />}
              value=""
            />
          )}
        </HeaderActions>
      </Header>
      <MessagesArea data-aid="teamAssistV2Messages" onScroll={onMessagesScroll} ref={messagesAreaRef}>
        {isEmpty && (
          <EmptyState>
            <EmptyStateIconWrapper>
              <TeamAssistEmptyStateIcon size={100} />
            </EmptyStateIconWrapper>
            <EmptyStateText>{t('Ask anything about this customer—answers, history, or next steps.')}</EmptyStateText>
          </EmptyState>
        )}
        {turns.map((turn, index) => (
          <TeamAssistV2Turn
            canRetry={index === turns.length - 1 && !isBusy}
            cdnHost={cdnHost}
            key={turn.id}
            onRate={rate}
            onRetry={retry}
            onUse={insertReply}
            orgId={orgId}
            turn={turn}
          />
        ))}
        {showLoadFailure && (
          <ErrorRow data-aid="teamAssistV2LoadError" role="alert">
            <ErrorIcon className="fa fa-exclamation-circle" />
            <ErrorText>{t("Team Assist couldn't load this customer's earlier questions.")}</ErrorText>
            <ErrorRetryButton data-aid="teamAssistV2ReloadButton" onClick={reload} type="button">
              <i aria-hidden="true" className="fa fa-repeat" /> {t('Retry')}
            </ErrorRetryButton>
          </ErrorRow>
        )}
        {isLoading && turns.length === 0 && (
          <AssistantRow>
            <TeamAssistLoadingDots />
          </AssistantRow>
        )}
      </MessagesArea>
      <VisuallyHidden aria-live="polite" role="status">
        {hasJustAnswered ? t('Team Assist answered.') : ''}
      </VisuallyHidden>
      <InputArea>
        {visibleCannedResponses.length > 0 && (
          <CannedResponseButtons>
            {visibleCannedResponses.map((cr, idx) => (
              <CannedResponseButton
                data-aid="teamAssistV2CannedResponseButton"
                disabled={isBusy}
                key={idx}
                onClick={() => ask(cr.message, cr.label)}
                type="button"
              >
                {cr.label}
              </CannedResponseButton>
            ))}
          </CannedResponseButtons>
        )}
        <TeamAssistInput
          dataAid="teamAssistV2Input"
          dataAidSend="teamAssistV2SendButton"
          disabled={!inputValue.trim() || isBusy}
          inputRef={inputRef}
          maxLength={TEAM_ASSIST_V2_QUESTION_MAX_LENGTH}
          onChange={evt => setInputValue(evt.target.value)}
          onKeyDown={onKeyDown}
          onSubmit={handleSubmit}
          value={inputValue}
        />
        <LengthHint aria-live="polite" data-aid="teamAssistV2LengthHint">
          {isAtLengthLimit
            ? t('Questions can be up to {{max}} characters.', {
                max: TEAM_ASSIST_V2_QUESTION_MAX_LENGTH.toLocaleString(i18n.resolvedLanguage || i18n.language),
              })
            : ''}
        </LengthHint>
      </InputArea>
    </StyledPanel>
  );
}

TeamAssistV2Panel.propTypes = {
  cannedResponses: PropTypes.arrayOf(
    PropTypes.shape({
      alwaysShow: PropTypes.bool,
      label: PropTypes.string,
      message: PropTypes.string,
    })
  ),
  cdnHost: PropTypes.string,
  conversationId: PropTypes.string,
  customerId: PropTypes.string,
  customerName: PropTypes.string,
  hasLoaded: PropTypes.bool,
  hasLoadFailed: PropTypes.bool,
  isClearing: PropTypes.bool,
  isLoading: PropTypes.bool,
  orgId: PropTypes.string,
  turns: PropTypes.arrayOf(
    PropTypes.shape({
      answer: PropTypes.object,
      displayedMessage: PropTypes.string,
      id: PropTypes.string.isRequired,
      live: PropTypes.bool,
      question: PropTypes.string,
    })
  ),
};

TeamAssistV2Panel.defaultProps = {
  cannedResponses: [],
  hasLoaded: false,
  hasLoadFailed: false,
  isClearing: false,
  isLoading: false,
  turns: [],
};

function visibleQuickActions(cannedResponses, { hasTurns, isLoading }) {
  if (hasTurns) {
    return cannedResponses.filter(cr => cr.alwaysShow);
  }
  return isLoading ? [] : cannedResponses;
}

const FOLLOW_THRESHOLD_PX = 40;

// Scrolls to each new turn (a retried one included), and keeps the newest
// answer in view as it streams unless the agent has scrolled up to read.
function useFollowNewestAnswer(messagesAreaRef, { lastAnswer, lastTurnId }) {
  const isFollowingRef = useRef(true);

  useEffect(() => {
    isFollowingRef.current = true;
  }, [lastTurnId]);

  useEffect(() => {
    const area = messagesAreaRef.current;
    if (area && isFollowingRef.current) {
      area.scrollTop = area.scrollHeight;
    }
  }, [lastAnswer, messagesAreaRef, lastTurnId]);

  return useCallback(() => {
    const area = messagesAreaRef.current;
    isFollowingRef.current = area.scrollHeight - area.scrollTop - area.clientHeight <= FOLLOW_THRESHOLD_PX;
  }, [messagesAreaRef]);
}

// Whether the newest answer finished streaming here, for the live region to
// announce; a session loaded already answered stays silent.
function useHasJustAnswered(lastAnswer) {
  const [hasJustAnswered, setHasJustAnswered] = useState(false);
  const wasStreamingRef = useRef(false);
  const status = lastAnswer ? lastAnswer.status : null;

  useEffect(() => {
    setHasJustAnswered(wasStreamingRef.current && status === TeamAssistV2AnswerStatus.done);
    wasStreamingRef.current = status === TeamAssistV2AnswerStatus.streaming;
  }, [status]);

  return hasJustAnswered;
}

function mapStateToProps({ getProvider }) {
  const currentLocation = getProvider('currentLocation').get();
  const profile = getProvider('profile').get();
  const teamAssistsV2 = getProvider('teamAssistsV2');
  const teamAssistConfig = getProvider('teamAssistConfiguration').get();
  const auth = getProvider('auth').get();

  return {
    cannedResponses: (teamAssistConfig && teamAssistConfig.cannedResponses) || [],
    cdnHost: gladlyCdn(),
    conversationId: currentLocation.currentConversationId,
    customerId: currentLocation.customerId,
    customerName: profile && profile.name,
    hasLoaded: teamAssistsV2.hasLoaded(),
    hasLoadFailed: Boolean(teamAssistsV2.getErrorForLoading()),
    isClearing: teamAssistsV2.isClearing(),
    isLoading: teamAssistsV2.isLoading(),
    orgId: auth && auth.getOrgId(),
    turns: teamAssistsV2.findAll() || [],
  };
}

// Mounts a fresh panel per customer, so a typed question, the scroll position,
// and the "answered" announcement don't carry over from the customer before.
export function TeamAssistV2PanelForCustomer(props) {
  return <TeamAssistV2Panel key={props.customerId} {...props} />;
}

TeamAssistV2PanelForCustomer.propTypes = {
  customerId: PropTypes.string,
};

export default connect(mapStateToProps)(TeamAssistV2PanelForCustomer);

const LengthHint = styled.div`
  color: ${p => p.theme.colors.gray600};
  font-size: ${p => p.theme.fontSize.small};

  &:not(:empty) {
    margin-top: 4px;
  }
`;
